import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig } from '@inavista/components/grid';
import { centreStatusColumns } from 'projects/service/src/app/shared/utils/grid-status-align';

// Service Order / ISC popup → Checklist popup on @inavista/components. Same columns, labels, widths and edit rule
// (Comments only, opened by a double click) as the Syncfusion grid it replaces. Rows are the popup's own `gridData`
// (LocalRowsGridApi, no endpoint); they are saved with the popup's Save buttons (Checklist/SaveChecklist), as before.

export const CHECKLIST_PERSISTENCE_KEY = 'rims.service.checklist';
export const CHECKLIST_URL = 'page://checklist';

// The old command buttons: text-success / text-danger.
const GREEN = '#198754';
const RED = '#dc3545';
const CHECK_ICON = '<polyline points="20 6 9 17 4 12" />';
const TIMES_ICON = '<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />';

export const CHECKLIST_COLUMNS: [string, string][] = [
  ['id', 'int'],
  ['value', 'nvarchar'],
  ['status', 'nvarchar'],
  ['comment', 'nvarchar'],
];

export interface ChecklistGridContext {
  /** The Status cell: the old coloured pill (grid-status-column, green / red / overdue). */
  statusCell: TemplateRef<unknown>;
  /** The old AllGrid_Toolbar_Excel_Export role claim. */
  canExport: boolean;
}

// The old Approve / Reject command buttons: always enabled; they set the row's status to their title.
export function buildChecklistActionIcons(): ActionIconDef[] {
  return [
    { key: 'Approved', label: 'Approved', icon: CHECK_ICON, colorFn: () => GREEN },
    { key: 'Rejected', label: 'Rejected', icon: TIMES_ICON, colorFn: () => RED },
  ];
}

export function buildChecklistGridConfig(ctx: ChecklistGridContext): GridConfig {
  return centreStatusColumns({
    apiBaseUrl: CHECKLIST_URL,
    clientSide: true,
    keyColumn: 'id',
    rowHeight: 30,
    defaultColumnOrder: CHECKLIST_COLUMNS.map(([c]) => c),
    // Every column was nonHideable in the old column chooser.
    alwaysVisibleColumns: CHECKLIST_COLUMNS.map(([c]) => c),
    columnLabels: { id: 'Sr No.', value: 'Description', status: 'Status', comment: 'Comments' },
    columnAlign: {
      // Sr No. is a number the user reads as a code: centred (RIMS grid rule); headers left.
      id: { header: { horizontal: 'left' } },
      value: { header: { horizontal: 'left' } },
      status: { header: { horizontal: 'left' } },
      comment: { header: { horizontal: 'left' } },
    },
    // The old widths; Actions was 80.
    columnWidths: { id: 65, value: 180, status: 65, comment: 170 },
    actionsColumnMinWidth: 80,
    cellTemplates: { status: ctx.statusCell },
    // The old toolbar: Columns, Search, Excel export (with the role claim), Clear filters. Reset layout is kept: it
    // undoes the saved layout.
    enableExport: ctx.canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    // Comments only, opened by a double click (the Syncfusion default). Enter, the tick or the popup's Save keeps it.
    editMode: 'inline',
    editSaveMode: 'row',
    editTrigger: 'doubleClick',
    editableColumns: ['comment'],
    deleteMode: 'host',
    rowDeletable: () => false,
    emptyStateMessage: 'No checklist items. Pick a checklist above.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CHECKLIST_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  });
}
